import { useContext, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Collapse,
  Heading,
  Image,
  Stack,
  Tab,
  TabList,
  Tabs,
  Text,
  useMediaQuery,
  Wrap,
} from "@chakra-ui/react";
import { TenantContext } from "../shared/contexts/tenantContext";
import {
  doctorFullName,
  doctorLanguages,
  doctorWorkplace,
} from "../shared/helpers/doctors";
import { useTranslations } from "../shared/i18n/localization";
import type { Doctor } from "../shared/types";
import { DoctorChoiceButton } from "./DoctorChoiceButton";
import { CrossBulletIcon } from "./icons/CrossBulletIcon";
import { LocationIcon } from "./icons/LocationIcon";
import { TopNavBar } from "./TopNavBar";

interface DoctorProfileHeaderProps {
  doctor: Doctor;
  tabIndex: number;
  setTabIndex: (newIndex: number) => void;
}

export const DoctorProfileHeader: React.FC<DoctorProfileHeaderProps> = ({
  doctor,
  tabIndex,
  setTabIndex,
}) => {
  const [isLargerThan800] = useMediaQuery("(min-width: 800px)");
  return isLargerThan800 ? (
    <DesktopHeader
      doctor={doctor}
      tabIndex={tabIndex}
      setTabIndex={setTabIndex}
    />
  ) : (
    <MobileHeader
      doctor={doctor}
      tabIndex={tabIndex}
      setTabIndex={setTabIndex}
    />
  );
};

const MobileHeader: React.FC<DoctorProfileHeaderProps> = ({
  doctor,
  tabIndex,
  setTabIndex,
}) => {
  const { translate } = useTranslations();

  const shrinkThreshold = 30;
  const [shrink, setShrink] = useState(false);

  useEffect(() => {
    const onScroll = () => setShrink(window.pageYOffset > shrinkThreshold);
    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Box
      as="header"
      position="fixed"
      top={0}
      backgroundColor="monochrome.white"
      width="full"
      boxShadow="md"
      zIndex={5}
    >
      <TopNavBar />
      <Box backgroundColor="primary.500" width="full" height={1} />
      <Box paddingX={5}>
        <Stack spacing={3} paddingY={4} align="center">
          <Stack
            direction="row"
            alignSelf="center"
            justify="center"
            align="center"
            spacing={shrink ? 3 : 5}
            maxWidth={600}
          >
            <Image
              borderRadius="full"
              boxSize={
                shrink
                  ? { base: "68px", xl: "120px" }
                  : { base: "120px", xl: "200px" }
              }
              src={doctor.display_settings?.photo_urls?.medium}
              alt={translate("doctorProfile.doctor_image_alt_text", {
                doctorName: doctorFullName(doctor),
                location: doctorWorkplace(doctor)?.address?.city,
              })}
              title={translate("doctorProfile.doctor_image_alt_text", {
                doctorName: doctorFullName(doctor),
                location: doctorWorkplace(doctor)?.address?.city,
              })}
              itemProp="image"
            />
            {shrink ? (
              <DoctorChoiceButton doctor={doctor} />
            ) : (
              <Stack>
                <CrossBulletIcon boxSize={5} />
                <Heading as="h1" variant="h2" itemProp="name">
                  {doctorFullName(doctor)}
                </Heading>
                <DoctorBadges doctor={doctor} />
              </Stack>
            )}
          </Stack>
          {!shrink && (
            <Stack spacing={5} align="center" maxWidth={600}>
              <Stack direction="row" alignSelf="start" align="center">
                <LocationIcon />
                <Text color="gray.600" itemProp="workLocation">
                  {doctorWorkplace(doctor)?.address?.city}
                </Text>
              </Stack>
              <Box>
                <Heading as="h2" variant="p1">
                  {translate("doctorProfile.header.welcomeMessage.title")}
                </Heading>
                <Text>
                  {translate("doctorProfile.header.welcomeMessage.body", {
                    doctorName: doctorFullName(doctor),
                  })}
                </Text>
              </Box>
            </Stack>
          )}
          {!shrink && (
            <Box paddingTop={5}>
              <DoctorChoiceButton doctor={doctor} />
            </Box>
          )}
        </Stack>
        <DoctorProfileTabs
          items={["intro", "info", "location", "faq"].map((key) =>
            translate(`doctorProfile.header.tabs.${key}`)
          )}
          tabIndex={tabIndex}
          setTabIndex={setTabIndex}
        />
      </Box>
    </Box>
  );
};

const DesktopHeader: React.FC<DoctorProfileHeaderProps> = ({
  doctor,
  tabIndex,
  setTabIndex,
}) => {
  const { translate } = useTranslations();

  const shrinkThreshold = 30;
  const [shrink, setShrink] = useState(false);

  useEffect(() => {
    const onScroll = () => setShrink(window.pageYOffset > shrinkThreshold);
    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Stack
      as="header"
      backgroundColor="monochrome.white"
      width="full"
      position="fixed"
      top={0}
      boxShadow="md"
      zIndex={5}
      spacing={0}
      alignItems="center"
    >
      <TopNavBar />
      <Box backgroundColor="primary.500" width="full" height={1} />
      <Stack
        direction="row"
        justify="space-between"
        align="end"
        spacing={8}
        paddingTop={5}
        paddingX={4}
        maxWidth="1080px"
        minWidth="800px"
        width="full"
      >
        <Image
          alignSelf="center"
          borderRadius="full"
          boxSize={shrink ? "130px" : "230px"}
          src={doctor.display_settings?.photo_urls?.medium}
          alt={translate("doctorProfile.doctor_image_alt_text", {
            doctorName: doctorFullName(doctor),
            location: doctorWorkplace(doctor)?.address?.city,
          })}
          title={translate("doctorProfile.doctor_image_alt_text", {
            doctorName: doctorFullName(doctor),
            location: doctorWorkplace(doctor)?.address?.city,
          })}
          itemProp="image"
        />
        <Stack spacing={1} width="full">
          <Stack direction="row" justifyContent="space-between">
            <Stack spacing={1}>
              <CrossBulletIcon boxSize={{ base: 5, xl: 7 }} />
              <Heading as="h1" variant="h1" itemProp="name">
                {doctorFullName(doctor)}
              </Heading>
            </Stack>
            <DoctorChoiceButton doctor={doctor} height="fit-content" />
          </Stack>
          <Stack spacing={5}>
            <Collapse in={!shrink} animateOpacity>
              <Stack direction="row" alignSelf="start" align="center">
                <LocationIcon />
                <Text color="gray.600" itemProp="workLocation">
                  {doctorWorkplace(doctor)?.address?.city}
                </Text>
                <DoctorBadges doctor={doctor} />
              </Stack>
              <Box>
                <Heading as="h2" variant="p1">
                  {translate("doctorProfile.header.welcomeMessage.title")}
                </Heading>
                <Text>
                  {translate("doctorProfile.header.welcomeMessage.body", {
                    doctorName: doctorFullName(doctor),
                  })}
                </Text>
              </Box>
            </Collapse>
            <DoctorProfileTabs
              items={["intro", "info", "location", "faq"].map((key) =>
                translate(`doctorProfile.header.tabs.${key}`)
              )}
              tabIndex={tabIndex}
              setTabIndex={setTabIndex}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

const DoctorBadges: React.FC<{ doctor: Doctor }> = ({ doctor }) => {
  const { translate } = useTranslations();
  const tenantConfig = useContext(TenantContext);
  const gkvDoctor = doctor.legal_information?.germany?.is_gkv_member ?? false;

  return (
    <>
      {tenantConfig.features.insurances && !gkvDoctor && (
        <Badge textTransform="none">
          {translate("doctorProfile.only_private_cases")}
        </Badge>
      )}
      {tenantConfig.enabledPatientLocales.length > 1 && (
        <Wrap as="ul" spacing={2}>
          {doctorLanguages(doctor).map((language) => (
            <Badge
              as="li"
              key={language}
              textTransform="none"
              itemProp="knowsLanguage"
            >
              {translate(`languages.${language}`)}
            </Badge>
          ))}
          {doctor.can_create_prescription && (
            <Badge
              backgroundColor="primary.50"
              alignSelf="start"
              textTransform="none"
            >
              {translate("doctorProfile.can_create_prescription")}
            </Badge>
          )}
        </Wrap>
      )}
    </>
  );
};

interface DoctorProfileTabsProps {
  items: string[];
  tabIndex: number;
  setTabIndex: (newIndex: number) => void;
}

/**
 * Navigation tabs for the content of a doctor profile.
 *
 * @component
 *
 * @note
 * All attributes of the Tabs component as well as the flexShrink
 * attribute of the Tab children were only put in place to make the tabs
 * scrollable, because Chakra UI 2.5.1 doesn't have scrollable tabs. If
 * you're using a newer version of Chakra UI and the tabs show some unexpected
 * behavior in some browser, feel free to remove these attributes and find a
 * simpler solution.
 */
const DoctorProfileTabs: React.FC<DoctorProfileTabsProps> = ({
  items,
  tabIndex,
  setTabIndex,
}) => (
  <Tabs
    index={tabIndex}
    onChange={setTabIndex}
    alignSelf="start"
    overflowY="hidden"
    sx={{
      scrollbarWidth: "none",
      "::-webkit-scrollbar": {
        display: "none",
      },
    }}
  >
    <TabList>
      {items.map((title) => (
        <Tab key={title} flexShrink={0}>
          {title}
        </Tab>
      ))}
    </TabList>
  </Tabs>
);
